import { useEffect, useMemo, useReducer, type ReactNode } from 'react';
import { builderReducer } from '../reducer';
import { clearDraft, loadDraft, saveDraft } from '../services/draftStorage';
import { type BuilderState, type CharacterDraft, createEmptyDraft } from '../types';
import { BuilderContext, type BuilderContextValue } from './builderContextValue';

interface BuilderProviderProps {
  children: ReactNode;
  /**
   * **FI-46bis** — Se passato, sovrascrive la `loadDraft()` da localStorage
   * con il draft fornito. Usato in **modalità edit retroattivo L1**, dove
   * il provider monta con un draft pre-popolato dal PG salvato (via
   * `characterToDraft(rewindToLevel(c, 1))`).
   *
   * In creazione normale (default) → undefined → caricamento da localStorage.
   */
  initialDraft?: CharacterDraft | undefined;
  /**
   * **FI-46bis** — Controlla l'autosave del draft su localStorage.
   *  - `true` (default, creazione normale): ogni cambio scrive in localStorage
   *    sotto la chiave standard `draft`. Permette al PG-in-creazione di
   *    sopravvivere a refresh.
   *  - `false` (edit-mode L1): NO autosave, evita di sovrascrivere un
   *    eventuale draft "nuovo PG" in corso (use case: l'utente stava
   *    creando un nuovo PG, poi va a editare L1 del PG salvato; al cancel
   *    edit, il draft "nuovo PG" deve essere intatto).
   */
  persistDraft?: boolean | undefined;
}

/**
 * Provider del builder. Da montare nella `BuilderPage` (e solo lì).
 *
 * **Autosave**: ogni cambio di `state.draft` viene scritto in localStorage
 * via `saveDraft`, **a meno che `persistDraft={false}`** (FI-46bis edit-mode).
 * Niente debouncing per ora — il volume di dati è piccolo e i cambi per
 * sessione sono decine, non migliaia. Se diventasse un problema, aggiungere
 * `useDebouncedEffect`.
 *
 * Gli hooks consumer (`useBuilderState`, `useBuilderDraft`, ...) vivono in
 * `./useBuilder.ts` per soddisfare `react-refresh/only-export-components`.
 */
export function BuilderProvider({
  children,
  initialDraft,
  persistDraft = true,
}: BuilderProviderProps) {
  const [state, dispatch] = useReducer(builderReducer, undefined, () =>
    initBuilderState(initialDraft),
  );

  useEffect(() => {
    if (!persistDraft) return;
    saveDraft(state.draft);
  }, [state.draft, persistDraft]);

  const value = useMemo<BuilderContextValue>(() => {
    return {
      state,
      dispatch,
      resetBuilder: () => {
        if (persistDraft) clearDraft();
        dispatch({ type: 'RESET_DRAFT' });
      },
    };
  }, [state, persistDraft]);

  return <BuilderContext.Provider value={value}>{children}</BuilderContext.Provider>;
}

/**
 * Inizializza lo state del builder.
 *
 * **FI-46bis** — Priorità di inizializzazione:
 *  1. `initialDraft` esplicito (edit-mode L1): usa quello, ignora localStorage
 *  2. `loadDraft()` da localStorage (creazione in corso): rehydration
 *  3. `createEmptyDraft()` (nuovo PG): UUID fresco, campi vuoti
 */
function initBuilderState(initialDraft: CharacterDraft | undefined): BuilderState {
  if (initialDraft) return { draft: initialDraft };
  const persisted = loadDraft();
  return { draft: persisted ?? createEmptyDraft() };
}
